import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAppState } from '../context/AppStateContext';
import { useHelp } from './HelpProvider';
import HelpAsk from './HelpAsk';
import HelpSpotlight, { findAnchor, Rect, useHelpRects } from './HelpSpotlight';
import { groupBySection, searchHowTos, sectionForRoute } from './helpSearch';
import { HelpSection, HowTo, SECTION_ROUTES } from './types';

/** The guide the window's "Contact support" link opens. */
const CONTACT_SUPPORT_KEY = 'support.contact';

// ── Geometry ────────────────────────────────────────────────────────────────
const MARGIN = 16;
const TOP_MARGIN = 58;      // clears the 50px fixed header
const LEFT_MARGIN = 96;     // clears the 76px icon rail
const DEFAULT_W = 360;
const BROWSE_H = 460;
const RUNNING_H = 260;
const MIN_W = 280;
const MIN_H = 180;
const MAX_W = 560;
const SHEET_MAX_WIDTH = 768; // at or below this the window becomes a bottom sheet
const CLEARANCE = 16;        // gap kept between the window and what it highlights

interface Box { x: number; y: number; w: number; h: number }

const WINDOW_STORAGE_KEY = 'help.window';

interface StoredWindow { box: Box; userPlaced: boolean; userSized: boolean }

const defaultBox = (h: number): Box => ({
  x: LEFT_MARGIN,
  y: Math.max(TOP_MARGIN, window.innerHeight - h - MARGIN),
  w: DEFAULT_W,
  h,
});

const clampBox = (box: Box): Box => {
  const w = Math.min(Math.max(box.w, MIN_W), Math.min(MAX_W, window.innerWidth - MARGIN * 2));
  const h = Math.min(Math.max(box.h, MIN_H), window.innerHeight - TOP_MARGIN - MARGIN);
  return {
    w,
    h,
    // At least a slice stays on screen: a window parked off the edge of a big
    // monitor must not vanish when the same session opens on a laptop.
    x: Math.min(Math.max(box.x, MARGIN - w + 120), window.innerWidth - 120),
    y: Math.min(Math.max(box.y, TOP_MARGIN), window.innerHeight - 60),
  };
};

const readStoredWindow = (): StoredWindow | null => {
  try {
    const raw = localStorage.getItem(WINDOW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredWindow;
    if (!parsed?.box) return null;
    return { ...parsed, box: clampBox(parsed.box) };
  } catch {
    return null;
  }
};

const writeStoredWindow = (value: StoredWindow) => {
  try {
    localStorage.setItem(WINDOW_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Blocked storage — the window just forgets where it was.
  }
};

// ── Avoidance ───────────────────────────────────────────────────────────────
const inflate = (r: Rect, by: number) => ({
  left: r.left - by, top: r.top - by, right: r.left + r.width + by, bottom: r.top + r.height + by,
});

const overlapArea = (box: Box, obstacle: ReturnType<typeof inflate>): number => {
  const x = Math.min(box.x + box.w, obstacle.right) - Math.max(box.x, obstacle.left);
  const y = Math.min(box.y + box.h, obstacle.bottom) - Math.max(box.y, obstacle.top);
  return x > 0 && y > 0 ? x * y : 0;
};

/** Overlap between a box and a set of already-inflated obstacles. */
const overlapWith = (box: Box, blocks: ReturnType<typeof inflate>[]): number =>
  blocks.reduce((sum, b) => sum + overlapArea(box, b), 0);

/**
 * Obstacles worth dodging.
 *
 * A target that fills most of the screen — the schedule grid, a posts list —
 * cannot be dodged: every corner overlaps it, so the window ends up wherever
 * the overlap is *least*, which is some unrelated part of the page. That is how
 * it came to park over the session detail panel and then sit on the button the
 * next step pointed at. Past this size the ring is the signal on its own and the
 * window stays where the user left it.
 */
const HUGE_OBSTACLE_SHARE = 0.45;

export const dodgeable = (obstacles: Rect[]): Rect[] => {
  const viewport = window.innerWidth * window.innerHeight;
  return obstacles.filter(o => (o.width * o.height) / viewport < HUGE_OBSTACLE_SHARE);
};

/**
 * Keeps the window off whatever the step is pointing at.
 *
 * `home` — wherever the user last dragged it, or the default corner — is always
 * the first candidate, so the window returns there as soon as a step stops
 * clashing. It only moves when it actually overlaps, which is what stops it
 * hopping around between steps.
 */
export const positionClearOf = (home: Box, obstacles: Rect[]): Box => {
  const blocks = dodgeable(obstacles).map(o => inflate(o, CLEARANCE));
  const overlap = (box: Box) => overlapWith(box, blocks);

  if (overlap(home) === 0) return home;

  const right = window.innerWidth - home.w - MARGIN;
  const bottom = window.innerHeight - home.h - MARGIN;
  const candidates: Box[] = [
    { ...home, x: LEFT_MARGIN, y: bottom },
    { ...home, x: right, y: bottom },
    { ...home, x: right, y: TOP_MARGIN },
    { ...home, x: LEFT_MARGIN, y: TOP_MARGIN },
  ];

  const distanceFromHome = (b: Box) => Math.hypot(b.x - home.x, b.y - home.y);
  let best = candidates[0];
  let bestScore = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    // Overlap first, then stay as near as possible to where the user put it.
    const score = overlap(candidate) * 1_000_000 + distanceFromHome(candidate);
    if (score < bestScore) { bestScore = score; best = candidate; }
  }
  return best;
};

// ── Copy ────────────────────────────────────────────────────────────────────
/** `**bold**` marks UI labels, spelled exactly as the screen spells them. */
const renderCopy = (text: string): React.ReactNode[] =>
  text.split(/\*\*(.+?)\*\*/g).map((part, i) => (
    i % 2 === 1
      ? <strong key={i}>{part}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));

/**
 * Is the user somewhere other than where this step happens?
 *
 * A step's route may name a tab (`/admin?tab=staff`), and the wrong tab counts
 * as the wrong place: the tab is where the step happens, and the ring can only
 * point at a strip of tabs, not at the one that is meant. Anything the route
 * does not mention — a filter, a child — is left alone, so a step never drags
 * the user off the record they were looking at.
 */
export const notThereYet = (route: string, pathname: string, search: string): boolean => {
  const [routePath, routeQuery] = route.split('?');
  if (pathname !== routePath) return true;
  if (!routeQuery) return false;
  const here = new URLSearchParams(search);
  const wanted = new URLSearchParams(routeQuery);
  let missing = false;
  wanted.forEach((value, key) => { if (here.get(key) !== value) missing = true; });
  return missing;
};

// ── Window ──────────────────────────────────────────────────────────────────
const HelpWindow: React.FC = () => {
  const help = useHelp();
  const { isLocked, activeUser } = useAppState();
  const location = useLocation();


  const stored = useRef<StoredWindow | null>(null);
  if (stored.current === null) stored.current = readStoredWindow();

  const [box, setBox] = useState<Box>(() => stored.current?.box ?? defaultBox(BROWSE_H));
  const [collapsed, setCollapsed] = useState(false);
  // True while the pointer is dragging or resizing: the window must follow the
  // cursor exactly, so the position transition is switched off.
  const [interacting, setInteracting] = useState(false);
  const [isSheet, setIsSheet] = useState(() => window.innerWidth <= SHEET_MAX_WIDTH);
  const userPlaced = useRef<boolean>(stored.current?.userPlaced ?? false);
  const userSized = useRef<boolean>(stored.current?.userSized ?? false);
  const home = useRef<Box>(stored.current?.box ?? defaultBox(BROWSE_H));
  const dragging = useRef(false);
  // Which step the window has already been placed for — see the placement
  // effect below.
  const settledFor = useRef<string>('');
  const settleRetried = useRef<string>('');
  // The live position, for the retry check below. A ref, not a dependency:
  // depending on the box would re-run placement every time placement moved it.
  const boxRef = useRef(box);

  boxRef.current = box;

  const { guide, mode, stepIndex, open, query } = help;
  const step = mode === 'running' && guide ? guide.steps[stepIndex] : undefined;
  const rects = useHelpRects(mode === 'running' ? step?.target : undefined);

  const persist = useCallback((next: Box) => {
    writeStoredWindow({ box: next, userPlaced: userPlaced.current, userSized: userSized.current });
  }, []);

  // Coalesces pointer moves onto animation frames, keeping the newest position
  // rather than the first of the batch — dropping the latest move is what makes
  // a drag feel like it is trailing the cursor.
  const pendingFrame = useRef(0);
  const pendingBox = useRef<(() => Box) | null>(null);
  const scheduleBox = useCallback((compute: () => Box) => {
    pendingBox.current = compute;
    if (pendingFrame.current) return;
    pendingFrame.current = requestAnimationFrame(() => {
      pendingFrame.current = 0;
      const next = pendingBox.current;
      pendingBox.current = null;
      if (next) setBox(next());
    });
  }, []);

  useEffect(() => () => { if (pendingFrame.current) cancelAnimationFrame(pendingFrame.current); }, []);

  // Sheet mode follows the viewport, not the device.
  useEffect(() => {
    const onResize = () => {
      setIsSheet(window.innerWidth <= SHEET_MAX_WIDTH);
      setBox(prev => clampBox(prev));
      // A resize moves everything, so the current step earns a fresh placement.
      settledFor.current = '';
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Running needs far less room than browsing. Only the app decides this while
  // the user hasn't resized the window themselves.
  useEffect(() => {
    if (userSized.current) return;
    const wanted = mode === 'running' ? RUNNING_H : BROWSE_H;
    setBox(prev => {
      if (prev.h === wanted) return prev;
      // Keep the bottom edge where it is, so a shrinking window doesn't crawl
      // up the screen between steps.
      const next = clampBox({ ...prev, h: wanted, y: prev.y + (prev.h - wanted) });
      home.current = { ...home.current, h: next.h, y: next.y };
      return next;
    });
  }, [mode]);

  // Move clear of the highlight (and of any dialog the app has opened) — but
  // AT MOST ONCE per step.
  //
  // The anchor's rectangle changes constantly as the user works: selecting a
  // session opens the detail panel and reflows the grid, a list scrolls, a
  // panel animates. Re-running the placement on every one of those made the
  // window hop around while the user was clicking things, which reads as the
  // window having a mind of its own. So placement is settled once per step,
  // and then left alone until something discrete happens: a different step, a
  // dialog opening, or the viewport resizing.
  const targetKey = Array.isArray(step?.target) ? step?.target.join('|') : step?.target;
  const settleKey = [guide?.key ?? '', stepIndex, mode, rects.modal ? 'modal' : ''].join(':');

  useEffect(() => {
    if (isSheet || dragging.current) return;
    if (settledFor.current === settleKey) {
      // Already placed for this step. Re-place only if the window has ended up
      // ON the target since — the panel that holds it opened late, or the layout
      // moved under us — and only once, so this can never become a loop.
      if (settleRetried.current === settleKey) return;
      const blocks = dodgeable(
        [rects.target, rects.modal].filter((r): r is Rect => r !== null),
      ).map(o => inflate(o, CLEARANCE));
      if (overlapWith(boxRef.current, blocks) === 0) return;
      settleRetried.current = settleKey;
    }
    // Wait for the anchor before placing: a step whose target hasn't rendered
    // yet would otherwise be placed against nothing, and the ring could then
    // appear underneath the window.
    if (mode === 'running' && step?.target && !rects.target && !rects.modal) return;

    settledFor.current = settleKey;
    const obstacles = [rects.target, rects.modal].filter((r): r is Rect => r !== null);
    const next = positionClearOf({ ...home.current, w: box.w, h: box.h }, obstacles);
    setBox(prev => (prev.x === next.x && prev.y === next.y ? prev : { ...prev, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settleKey, rects.target, rects.modal, box.w, box.h, isSheet, mode, targetKey]);

  // Bring the anchor into view — a ring below the fold helps nobody.
  const scrollToAnchor = () => {
    if (!step?.target) return;
    const anchor = findAnchor(step.target);
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const offscreen = r.top < TOP_MARGIN || r.bottom > window.innerHeight - MARGIN;
    if (offscreen) anchor.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  // Once when a step opens.
  useEffect(() => {
    if (mode !== 'running') return;
    scrollToAnchor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, targetKey, guide?.key, stepIndex]);

  // "Show me" presses, tied to the ring they were for. Keyed rather than reset
  // in an effect so a new step can never render even one frame with the
  // previous step's count — that frame would play the locate animation where
  // the ordinary arrival pulse belongs.
  const arriveKey = `${guide?.key ?? ''}:${stepIndex}:${targetKey ?? ''}`;
  const [locateFor, setLocateFor] = useState<{ key: string; count: number }>({ key: '', count: 0 });
  const locate = locateFor.key === arriveKey ? locateFor.count : 0;
  const showMe = () => {
    scrollToAnchor();
    setLocateFor({ key: arriveKey, count: locate + 1 });
  };

  // ── Drag & resize ─────────────────────────────────────────────────────────
  const startDrag = (e: React.PointerEvent) => {
    if (isSheet || e.button !== 0) return;
    const origin = { x: e.clientX, y: e.clientY };
    const start = box;
    dragging.current = true;
    setInteracting(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

    const onMove = (ev: PointerEvent) => {
      // One update per frame: a pointer emits moves faster than the browser
      // paints, and each surplus one is a wasted render.
      scheduleBox(() => clampBox({
        ...start, x: start.x + ev.clientX - origin.x, y: start.y + ev.clientY - origin.y,
      }));
    };
    const onUp = () => {
      dragging.current = false;
      setInteracting(false);
      userPlaced.current = true;
      setBox(prev => { home.current = prev; persist(prev); return prev; });
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const startResize = (e: React.PointerEvent) => {
    if (isSheet || e.button !== 0) return;
    e.stopPropagation();
    const origin = { x: e.clientX, y: e.clientY };
    const start = box;
    setInteracting(true);

    const onMove = (ev: PointerEvent) => {
      scheduleBox(() => clampBox({
        ...start, w: start.w + ev.clientX - origin.x, h: start.h + ev.clientY - origin.y,
      }));
    };
    const onUp = () => {
      setInteracting(false);
      userSized.current = true;
      setBox(prev => { home.current = prev; persist(prev); return prev; });
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ── Lists ─────────────────────────────────────────────────────────────────
  const results = useMemo(
    () => (query.trim() ? searchHowTos(help.visible, query) : []),
    [help.visible, query],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); help.closeHelp(); return; }
    if (mode !== 'running') return;
    if (e.key === 'ArrowRight') help.nextStep();
    if (e.key === 'ArrowLeft') help.prevStep();
  };

  if (!open || isLocked) return null;

  const title =
    mode === 'running' && guide ? guide.title
      : mode === 'preview' && guide ? guide.title
        : 'Help & How Tos';

  const renderList = (items: HowTo[]) => (
    <ul className="help-list">
      {items.map(item => (
        <li key={item.key}>
          <button type="button" className="help-list__item" onClick={() => help.previewHowTo(item.key)}>
            <span className="help-list__title">{item.title}</span>
            <span className="help-list__summary">{item.summary}</span>
          </button>
        </li>
      ))}
    </ul>
  );

  const renderGroups = (items: HowTo[], first?: HelpSection) => (
    <>
      {groupBySection(items, first).map(group => {
        // The heading stays a label — clicking a whole group heading to move
        // pages is too easy to do by accident. The jump is a separate, small
        // control beside it, always visible, so a list of results doesn't have
        // to be swept to find which of them can be jumped to. A section only
        // appears here when it holds a How To this user can follow, so the
        // page is one they can open.
        const route = SECTION_ROUTES[group.section];
        return (
          <section className="help-group" key={group.section}>
            <div className="help-group__head">
              <h6 className="help-group__title">{group.section}</h6>
              {route && route !== location.pathname && (
                <button
                  type="button"
                  className="help-group__go"
                  onClick={() => help.requestNavigate(route)}
                  title={`Go to ${group.section}`}
                  aria-label={`Go to ${group.section}`}
                >
                  <i className="fas fa-chevron-right" aria-hidden />
                </button>
              )}
            </div>
            {renderList(group.items)}
          </section>
        );
      })}
    </>
  );

  // Shown while a detour is in progress, in every mode: the way back must be
  // visible throughout, not just when the detour happens to end.
  const breadcrumb = help.returnGuide && help.returnTo && (
    <button type="button" className="help-breadcrumb" onClick={help.returnFromSeeAlso}>
      <i className="fas fa-arrow-turn-up help-breadcrumb__icon" aria-hidden />
      <span>
        Back to <strong>{help.returnGuide.title}</strong>
        <span className="help-breadcrumb__step">
          Step {help.returnTo.stepIndex + 1} of {help.returnGuide.steps.length}
        </span>
      </span>
    </button>
  );

  const body = (() => {
    if (mode === 'preview' && guide) {
      return (
        // The steps list is as long as the How To; Start is pinned below it so
        // it never scrolls out of reach on a short window.
        <div className="help-window__body help-preview">
          <div className="help-preview__scroll">
            {breadcrumb}
            <button type="button" className="help-back" onClick={help.showBrowse}>
              <i className="fas fa-arrow-left" aria-hidden /> All How Tos
            </button>
            <h5 className="help-preview__title">{guide.title}</h5>
            <p className="help-preview__summary">{guide.summary}</p>
            {guide.youNeed && guide.youNeed.length > 0 && (
              <div className="help-need">
                <h6>You'll need</h6>
                <ul>{guide.youNeed.map(n => <li key={n}>{n}</li>)}</ul>
              </div>
            )}
            <h6 className="help-preview__steps-title">Steps</h6>
            <ol className="help-steps">
              {guide.steps.map((s, i) => <li key={i}>{renderCopy(s.text)}</li>)}
            </ol>
            {guide.related && guide.related.length > 0 && (
              <div className="help-related">
                <h6>Related</h6>
                {renderList(help.visible.filter(h => guide.related?.includes(h.key)))}
              </div>
            )}
          </div>
          <div className="help-preview__footer">
            {help.started ? (
              <div className="help-preview__actions">
                <button type="button" className="help-primary" onClick={help.resumeGuide}>
                  Resume at step {stepIndex + 1}
                </button>
                <button type="button" className="help-secondary" onClick={() => help.startHowTo(guide.key)}>
                  Start again
                </button>
              </div>
            ) : (
              <button type="button" className="help-primary help-preview__start" onClick={() => help.startHowTo(guide.key)}>
                Start
              </button>
            )}
          </div>
        </div>
      );
    }

    if (mode === 'running' && guide && step) {
      const last = stepIndex === guide.steps.length - 1;
      const elsewhere = !!step.route && notThereYet(step.route, location.pathname, location.search);
      const missingAnchor = !!step.target && !rects.target;
      return (
        <div className="help-window__body help-run">
          <div className="help-run__meta">Step {stepIndex + 1} of {guide.steps.length}</div>
          <div className="help-run__text">{renderCopy(step.text)}</div>
          {/* Whenever there's a ring to find — including a "go to this page"
              step, where the target is the menu item and is exactly the small
              thing that's easy to lose. */}
          {rects.target && (
            <button type="button" className="help-link" onClick={showMe}>
              <i className="fas fa-crosshairs" aria-hidden /> Show me
            </button>
          )}
          {elsewhere && (
            <button
              type="button"
              className="help-secondary"
              // Arriving completes the step only when getting here was all it
              // asked (`navigateOnly`); otherwise the reader lands on the page
              // with the step still showing, ring and all. And only on a move
              // that actually happened: if the page put up its discard prompt
              // and the user said no, they're still on the old page and the
              // step still stands.
              onClick={() => {
                const advance = step.navigateOnly === true;
                void help.requestNavigate(step.route!).then(moved => { if (moved && advance) help.nextStep(); });
              }}
            >
              <i className="fas fa-arrow-right" aria-hidden /> Take me there
            </button>
          )}
          {step.seeAlso && (
            <HelpAsk
              guideKey={step.seeAlso.guideKey}
              label={step.seeAlso.label}
              variant="inline"
              onFollow={help.followSeeAlso}
            />
          )}
          {missingAnchor && !elsewhere && (
            <p className="help-run__hint">
              Can't see it highlighted? It isn't on this screen yet — follow the step above.
            </p>
          )}
          {breadcrumb}
          <div className="help-run__actions">
            <button
              type="button"
              className="help-secondary"
              onClick={help.prevStep}
              disabled={stepIndex === 0}
            >
              Back
            </button>
            <button
              type="button"
              className="help-primary"
              onClick={() => {
                if (!last) { help.nextStep(); return; }
                // Finishing a detour returns to the step that sent the user
                // here — closing help on them would throw away the guide they
                // were part-way through.
                if (help.returnTo) { help.returnFromSeeAlso(); return; }
                help.endGuide();
                help.closeHelp();
              }}
            >
              {last ? (help.returnTo ? 'Finish & go back' : 'Finish') : 'Next'}
            </button>
          </div>
          {/* Browsing away keeps your place — the list then offers to resume.
              Stopping is the explicit way out, for when the user is done with
              this How To but not with help. */}
          <div className="help-run__links">
            <button type="button" className="help-link" onClick={help.showBrowse}>
              All How Tos
            </button>
            <button type="button" className="help-link help-link--quiet" onClick={help.endGuide}>
              Stop this How To
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="help-window__body">
        {/* The query survives closing the window, so a filtered list can be
            what greets the user next time. It has to announce itself: the
            field is tinted while it holds anything, and the bar below says
            what is being hidden and offers one click out of it. */}
        <div className={`help-search-row${query.trim() ? ' is-filtering' : ''}`}>
          <i className="fas fa-search help-search-row__icon" aria-hidden />
          <input
            type="search"
            className="help-search"
            placeholder="Search How Tos…"
            value={query}
            onChange={e => help.setQuery(e.target.value)}
            aria-label="Search How Tos"
          />
          {query !== '' && (
            <button
              type="button"
              className="help-search-row__clear"
              onClick={() => help.setQuery('')}
              title="Clear the search"
              aria-label="Clear the search"
            >
              <i className="fas fa-times" aria-hidden />
            </button>
          )}
        </div>
        {query.trim() && (
          <div className="help-filter-bar">
            <span>
              {results.length === 0
                ? <>No matches for <strong>“{query.trim()}”</strong></>
                : <>Showing {results.length} of {help.visible.length} How Tos, matching <strong>“{query.trim()}”</strong></>}
            </span>
            <button type="button" className="help-filter-bar__clear" onClick={() => help.setQuery('')}>
              Show all
            </button>
          </div>
        )}
        {breadcrumb}
        {guide && help.started && (
          <div className="help-resume">
            <button type="button" className="help-resume__go" onClick={help.resumeGuide}>
              <i className="fas fa-rotate-left" aria-hidden />
              <span>
                Resume: {guide.title}
                <span className="help-resume__step">Step {stepIndex + 1} of {guide.steps.length}</span>
              </span>
            </button>
            <button
              type="button"
              className="help-resume__drop"
              onClick={help.endGuide}
              title="Forget this How To"
              aria-label="Forget this How To"
            >
              <i className="fas fa-times" aria-hidden />
            </button>
          </div>
        )}
        {query.trim() ? (
          results.length > 0 ? renderGroups(results) : (
            <p className="help-empty">
              No How Tos match “{query.trim()}”. Try different words, or browse the sections below.
            </p>
          )
        ) : (
          /* One list, grouped by section, with the section for the page you
             are on lifted to the top. It used to be two — "On this page" and
             then "All How Tos" — which printed the current page's guides
             twice, under two different headings, every time. The section name
             says where you are well enough on its own.

             A guide with no routes is deliberately unlisted: whether it
             applies is something only the app can know (Set up your PIN turns
             on `hasPin`), so it is offered at the point of need and found by
             searching, never browsed into. */
          renderGroups(
            help.visible.filter(h => h.routes.length > 0),
            sectionForRoute(location.pathname),
          )
        )}
        {/* Always a next step when the list has no answer. It opens the guide
            rather than an email, so the rules (no children's names; quote the
            Ref) are read before anything is written. Hidden from whoever the
            guide is hidden from — externals ask their setting, not us. */}
        {help.visible.some(h => h.key === CONTACT_SUPPORT_KEY) && (
          <p className="help-support">
            Can't find what you need?{' '}
            <button type="button" className="help-link" onClick={() => help.previewHowTo(CONTACT_SUPPORT_KEY)}>
              Contact support
            </button>
          </p>
        )}
      </div>
    );
  })();

  const style: React.CSSProperties = isSheet
    ? {}
    : { left: box.x, top: box.y, width: box.w, height: collapsed ? undefined : box.h };

  return createPortal(
    <>
      {mode === 'running' && (
        <HelpSpotlight rect={rects.target} arriveKey={arriveKey} locate={locate} />
      )}
      <div
        className={`help-window${isSheet ? ' is-sheet' : ''}${collapsed ? ' is-collapsed' : ''}${interacting ? ' is-interacting' : ''}`}
        style={style}
        role="dialog"
        aria-modal="false"
        aria-label="Help and How Tos"
        onKeyDown={onKeyDown}
      >
        <div className="help-window__bar" onPointerDown={startDrag}>
          <i className="fas fa-circle-question help-window__icon" aria-hidden />
          <span className="help-window__title" title={title}>{title}</span>
          <button
            type="button"
            className="help-window__chip"
            onPointerDown={e => e.stopPropagation()}
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand' : 'Collapse'}
            aria-label={collapsed ? 'Expand help' : 'Collapse help'}
          >
            <i className={`fas ${collapsed ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden />
          </button>
          <button
            type="button"
            className="help-window__chip"
            onPointerDown={e => e.stopPropagation()}
            onClick={help.closeHelp}
            title="Close help"
            aria-label="Close help"
          >
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>
        {!collapsed && body}
        {!isSheet && !collapsed && (
          <div
            className="help-window__grip"
            onPointerDown={startResize}
            role="presentation"
            title="Resize"
          />
        )}
      </div>
    </>,
    document.body,
  );
};

export default HelpWindow;
